import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Download,
  Calendar,
  CreditCard,
  Receipt,
  BarChart3,
  Filter,
  Settings,
  Landmark,
  Building2,
} from 'lucide-react';
import { financeiroService, ResumoFinanceiro } from '@/services/financeiroService';
import { toast } from 'sonner';
import {
  ContasPagar,
  ContasReceber,
  ContratosAberto,
  BancoInter,
} from '@/components/financeiro';
import { DashboardFinanceiro } from '@/components/financeiro/DashboardFinanceiro';
import { ConfiguracoesFinanceiro } from '@/components/financeiro/ConfiguracoesFinanceiro';
import { ProntidaoContabilCard } from '@/components/financeiro/ProntidaoContabilCard';
import { LancamentoManualBanco } from '@/components/financeiro/LancamentoManualBanco';
import { formatarCnpj, useEmpresaFinanceira } from '@/contexts/EmpresaFinanceiraContext';
import { supabase } from '@/integrations/supabase/client';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { ptBR } from 'date-fns/locale';

function PainelExtrato() {
  const { empresa, contaBancaria } = useEmpresaFinanceira();
  if (!empresa || !contaBancaria) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Extrato</CardTitle>
          <CardDescription>
            Selecione RST Serviços ou Sun7 Energy. O consolidado junta os dois caixas e não abre o extrato.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  switch (contaBancaria.modo) {
    case 'api':
      return <BancoInter />;
    case 'manual':
      return <LancamentoManualBanco />;
    default: {
      const modoInesperado: never = contaBancaria.modo;
      return modoInesperado;
    }
  }
}

export default function Financeiro() {
  const isMobile = useIsMobile();
  const { empresas, selecao, setSelecao, empresa } = useEmpresaFinanceira();
  const [tabAtiva, setTabAtiva] = useState('dashboard');
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null);
  const [loading, setLoading] = useState(true);
  const [relatorioDialogOpen, setRelatorioDialogOpen] = useState(false);
  const [periodoRelatorio, setPeriodoRelatorio] = useState<'mes' | 'trimestre' | 'ano'>('mes');
  const [tipoRelatorio, setTipoRelatorio] = useState<'completo' | 'receitas' | 'despesas' | 'fluxo' | 'contabilidade'>('completo');
  const [mesRelatorio, setMesRelatorio] = useState<number>(new Date().getMonth());
  const [anoRelatorio, setAnoRelatorio] = useState<number>(new Date().getFullYear());
  const [trimestreRelatorio, setTrimestreRelatorio] = useState<number>(Math.ceil((new Date().getMonth() + 1) / 3));
  const [periodoDashboard, setPeriodoDashboard] = useState<string>('mes_atual');
  const [mesSelecionado, setMesSelecionado] = useState<number>(new Date().getMonth());
  const [anoSelecionado, setAnoSelecionado] = useState<number>(new Date().getFullYear());
  const [dataInicioCustom, setDataInicioCustom] = useState<string>();
  const [dataFimCustom, setDataFimCustom] = useState<string>();
  const hojeRef = new Date();
  const inicioPadraoPersonalizado = startOfMonth(subMonths(hojeRef, 5));
  const fimPadraoPersonalizado = endOfMonth(hojeRef);
  const [mesInicioPersonalizado, setMesInicioPersonalizado] = useState<number>(inicioPadraoPersonalizado.getMonth());
  const [anoInicioPersonalizado, setAnoInicioPersonalizado] = useState<number>(inicioPadraoPersonalizado.getFullYear());
  const [mesFimPersonalizado, setMesFimPersonalizado] = useState<number>(fimPadraoPersonalizado.getMonth());
  const [anoFimPersonalizado, setAnoFimPersonalizado] = useState<number>(fimPadraoPersonalizado.getFullYear());

  useEffect(() => {
    carregarResumo();
  }, []);

  useEffect(() => {
    if (periodoDashboard !== 'periodo_personalizado') {
      setDataInicioCustom(undefined);
      setDataFimCustom(undefined);
      return;
    }

    const inicio = startOfMonth(new Date(anoInicioPersonalizado, mesInicioPersonalizado, 1));
    const fim = endOfMonth(new Date(anoFimPersonalizado, mesFimPersonalizado, 1));

    if (inicio > fim) return;

    setDataInicioCustom(format(inicio, 'yyyy-MM-dd'));
    setDataFimCustom(format(fim, 'yyyy-MM-dd'));
  }, [periodoDashboard, mesInicioPersonalizado, anoInicioPersonalizado, mesFimPersonalizado, anoFimPersonalizado]);

  const periodoPersonalizadoInvalido =
    periodoDashboard === 'periodo_personalizado' &&
    startOfMonth(new Date(anoInicioPersonalizado, mesInicioPersonalizado, 1)) >
      endOfMonth(new Date(anoFimPersonalizado, mesFimPersonalizado, 1));

  const carregarResumo = async () => {
    try {
      setLoading(true);
      const hoje = new Date();
      const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
      const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
      
      const dados = await financeiroService.obterResumoFinanceiro(
        inicioMes.toISOString().split('T')[0],
        fimMes.toISOString().split('T')[0]
      );
      setResumo(dados);
    } catch (error) {
      console.error('Erro ao carregar resumo financeiro:', error);
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const gerarRelatorio = async () => {
    if (!empresa) {
      toast.error('Selecione RST Serviços ou Sun7 Energy. O relatório não mistura as duas empresas.');
      return;
    }
    try {
      toast.info(`Gerando relatório de ${empresa.nome_fantasia}...`);
      
      let dataInicio: string, dataFim: string;

      if (periodoRelatorio === 'mes') {
        const dataRef = new Date(anoRelatorio, mesRelatorio, 1);
        dataInicio = format(startOfMonth(dataRef), 'yyyy-MM-dd');
        dataFim = format(endOfMonth(dataRef), 'yyyy-MM-dd');
      } else if (periodoRelatorio === 'trimestre') {
        const mesInicio = (trimestreRelatorio - 1) * 3;
        const mesFim = trimestreRelatorio * 3 - 1;
        dataInicio = format(new Date(anoRelatorio, mesInicio, 1), 'yyyy-MM-dd');
        dataFim = format(endOfMonth(new Date(anoRelatorio, mesFim, 1)), 'yyyy-MM-dd');
      } else {
        dataInicio = format(new Date(anoRelatorio, 0, 1), 'yyyy-MM-dd');
        dataFim = format(new Date(anoRelatorio, 11, 31), 'yyyy-MM-dd');
      }

      const response = await fetchWithAuth(`/api/relatorios/financeiro?dataInicio=${dataInicio}&dataFim=${dataFim}&periodo=${periodoRelatorio}&tipo=${tipoRelatorio}&empresaId=${empresa.id}`, {
        headers: { Accept: 'application/pdf' },
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Erro ao gerar relatório no servidor');
      }

      const tipoNome = tipoRelatorio === 'receitas' ? 'Receitas' : 
                       tipoRelatorio === 'despesas' ? 'Despesas' : 
                       tipoRelatorio === 'fluxo' ? 'Fluxo_de_Caixa' : 
                       tipoRelatorio === 'contabilidade' ? 'Contabilidade' : 'Financeiro_Completo';

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const nomeArquivo = empresa.nome_fantasia
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^A-Za-z0-9]+/g, '_');
      link.download = `${nomeArquivo}_Relatorio_${tipoNome}_${dataInicio}_a_${dataFim}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Relatório gerado com sucesso!');
      setRelatorioDialogOpen(false);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      toast.error('Erro ao gerar relatório profissional');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Financeiro</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">
            {empresa
              ? `${empresa.razao_social} — CNPJ ${formatarCnpj(empresa.cnpj)}`
              : 'Visão consolidada. Relatórios e envio contábil exigem uma empresa.'}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {selecao && (
            <Select value={selecao} onValueChange={setSelecao}>
              <SelectTrigger className="w-full sm:w-[240px] h-8 sm:h-10">
                <Building2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2 text-primary flex-shrink-0" />
                <SelectValue placeholder="Empresa" />
              </SelectTrigger>
              <SelectContent>
                {empresas.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.nome_fantasia}
                  </SelectItem>
                ))}
                <SelectItem value="consolidado">Consolidado</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Button onClick={() => setRelatorioDialogOpen(true)} className="gap-1 sm:gap-2 text-xs sm:text-sm h-8 sm:h-10">
            <Download className="h-3 w-3 sm:h-4 sm:w-4" />
            Relatórios
          </Button>
        </div>
      </div>

      {/* Tabs com as seções - breakpoint 990px em CSS para evitar corte/sobreposição */}
      <Tabs value={tabAtiva} onValueChange={setTabAtiva} className="space-y-4">
        <div className="flex flex-col gap-3 sm:gap-4">
          {/* Abaixo de 990px: grid 2x2; a partir de 990px: 5 colunas em uma linha */}
          <TabsList className="grid w-full grid-cols-2 min-[990px]:grid-cols-6 h-auto min-h-10 gap-1 p-1">
            <TabsTrigger value="dashboard" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <BarChart3 className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="contas-pagar" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <Receipt className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Contas a Pagar</span>
            </TabsTrigger>
            <TabsTrigger value="contas-receber" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <TrendingUp className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Contas a Receber</span>
            </TabsTrigger>
            <TabsTrigger value="contratos" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <FileText className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Contratos</span>
            </TabsTrigger>
            <TabsTrigger value="banco-inter" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <Landmark className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Extrato</span>
            </TabsTrigger>
            <TabsTrigger value="configuracoes" className="flex items-center justify-center gap-1 min-[990px]:gap-2 text-xs min-[990px]:text-sm px-2 py-2 min-[990px]:px-3 min-[990px]:py-1.5">
              <Settings className="h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 flex-shrink-0" />
              <span className="truncate">Configurações</span>
            </TabsTrigger>
          </TabsList>

          {/* Seletor de Período para Dashboard */}
          {tabAtiva === 'dashboard' && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              <Select value={periodoDashboard} onValueChange={(value) => setPeriodoDashboard(value)}>
                <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[200px] h-9'}`}>
                  <Calendar className={`${isMobile ? 'h-3 w-3' : 'h-4 w-4'} mr-2 text-primary flex-shrink-0`} />
                  <SelectValue placeholder="Selecione o período" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes_atual">Mês Corrente</SelectItem>
                  <SelectItem value="mes_especifico">Mês Específico</SelectItem>
                  <SelectItem value="ano_atual">Ano Corrente</SelectItem>
                  <SelectItem value="ultimo_semestre">Último Semestre</SelectItem>
                  <SelectItem value="ultimos_12_meses">Últimos 12 Meses</SelectItem>
                  <SelectItem value="periodo_personalizado">Período Personalizado</SelectItem>
                </SelectContent>
              </Select>

              {periodoDashboard === 'mes_especifico' && (
                <div className="flex items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-300">
                  <Select 
                    value={mesSelecionado.toString()} 
                    onValueChange={(v) => setMesSelecionado(parseInt(v))}
                  >
                    <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[130px] h-9'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {format(new Date(2024, i, 1), 'MMMM', { locale: ptBR })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select 
                    value={anoSelecionado.toString()} 
                    onValueChange={(v) => setAnoSelecionado(parseInt(v))}
                  >
                    <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[100px] h-9'}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 5 }).map((_, i) => {
                        const ano = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={ano} value={ano.toString()}>
                            {ano}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {periodoDashboard === 'periodo_personalizado' && (
                <div className="flex flex-col gap-2 animate-in fade-in slide-in-from-left-2 duration-300">
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                    <span className="text-xs text-muted-foreground font-medium px-1">De</span>
                    <Select
                      value={mesInicioPersonalizado.toString()}
                      onValueChange={(v) => setMesInicioPersonalizado(parseInt(v))}
                    >
                      <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[130px] h-9'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }).map((_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {format(new Date(2024, i, 1), 'MMMM', { locale: ptBR })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={anoInicioPersonalizado.toString()}
                      onValueChange={(v) => setAnoInicioPersonalizado(parseInt(v))}
                    >
                      <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[100px] h-9'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 6 }).map((_, i) => {
                          const ano = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={ano} value={ano.toString()}>
                              {ano}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>

                    <span className="text-xs text-muted-foreground font-medium px-1">Até</span>
                    <Select
                      value={mesFimPersonalizado.toString()}
                      onValueChange={(v) => setMesFimPersonalizado(parseInt(v))}
                    >
                      <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[130px] h-9'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 12 }).map((_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {format(new Date(2024, i, 1), 'MMMM', { locale: ptBR })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={anoFimPersonalizado.toString()}
                      onValueChange={(v) => setAnoFimPersonalizado(parseInt(v))}
                    >
                      <SelectTrigger className={`${isMobile ? 'w-full h-8 text-xs' : 'w-[100px] h-9'}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 6 }).map((_, i) => {
                          const ano = new Date().getFullYear() - i;
                          return (
                            <SelectItem key={ano} value={ano.toString()}>
                              {ano}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {periodoPersonalizadoInvalido && (
                    <p className="text-xs text-destructive px-1">
                      O mês inicial deve ser anterior ou igual ao mês final.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <TabsContent value="dashboard" className="space-y-4">
          <ProntidaoContabilCard />
          <DashboardFinanceiro 
            periodo={periodoDashboard}
            mes={mesSelecionado}
            ano={anoSelecionado}
            dataInicio={dataInicioCustom}
            dataFim={dataFimCustom}
            onNavigate={setTabAtiva}
          />
        </TabsContent>

        <TabsContent value="contas-pagar" className="space-y-4">
          <ContasPagar />
        </TabsContent>

        <TabsContent value="contas-receber" className="space-y-4">
          <ContasReceber />
        </TabsContent>

        <TabsContent value="contratos" className="space-y-4">
          <ContratosAberto tabAtiva={tabAtiva} />
        </TabsContent>

        <TabsContent value="banco-inter" className="space-y-4">
          <PainelExtrato />
        </TabsContent>

        <TabsContent value="configuracoes" className="space-y-4">
          <ConfiguracoesFinanceiro />
        </TabsContent>
      </Tabs>

      {/* Dialog de Relatório */}
      <Dialog open={relatorioDialogOpen} onOpenChange={setRelatorioDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Gerar Relatório Financeiro</DialogTitle>
            <DialogDescription>
              {empresa
                ? `Somente os lançamentos de ${empresa.nome_fantasia} — CNPJ ${formatarCnpj(empresa.cnpj)}.`
                : 'Selecione RST Serviços ou Sun7 Energy no topo da página. O consolidado não gera relatório.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Período</label>
              <Select value={periodoRelatorio} onValueChange={(value: 'mes' | 'trimestre' | 'ano') => setPeriodoRelatorio(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mes">Mensal</SelectItem>
                  <SelectItem value="trimestre">Trimestral</SelectItem>
                  <SelectItem value="ano">Anual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {periodoRelatorio === 'mes' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Mês do relatório</label>
                <div className="flex gap-2">
                  <Select value={mesRelatorio.toString()} onValueChange={(v) => setMesRelatorio(parseInt(v))}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      {Array.from({ length: 12 }).map((_, i) => (
                        <SelectItem key={i} value={i.toString()}>
                          {format(new Date(2024, i, 1), 'MMMM', { locale: ptBR })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={anoRelatorio.toString()} onValueChange={(v) => setAnoRelatorio(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 6 }).map((_, i) => {
                        const ano = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={ano} value={ano.toString()}>
                            {ano}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {periodoRelatorio === 'trimestre' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Trimestre do relatório</label>
                <div className="flex gap-2">
                  <Select value={trimestreRelatorio.toString()} onValueChange={(v) => setTrimestreRelatorio(parseInt(v))}>
                    <SelectTrigger className="flex-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                      <SelectItem value="2">Q2 (Abr-Jun)</SelectItem>
                      <SelectItem value="3">Q3 (Jul-Set)</SelectItem>
                      <SelectItem value="4">Q4 (Out-Dez)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={anoRelatorio.toString()} onValueChange={(v) => setAnoRelatorio(parseInt(v))}>
                    <SelectTrigger className="w-[100px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 6 }).map((_, i) => {
                        const ano = new Date().getFullYear() - i;
                        return (
                          <SelectItem key={ano} value={ano.toString()}>
                            {ano}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {periodoRelatorio === 'ano' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Ano do relatório</label>
                <Select value={anoRelatorio.toString()} onValueChange={(v) => setAnoRelatorio(parseInt(v))}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 6 }).map((_, i) => {
                      const ano = new Date().getFullYear() - i;
                      return (
                        <SelectItem key={ano} value={ano.toString()}>
                          {ano}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <label className="text-sm font-medium">Tipo de Relatório</label>
              <Select value={tipoRelatorio} onValueChange={(value: any) => setTipoRelatorio(value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="completo">Completo</SelectItem>
                  <SelectItem value="receitas">Apenas Receitas</SelectItem>
                  <SelectItem value="despesas">Apenas Despesas</SelectItem>
                  <SelectItem value="fluxo">Fluxo de Caixa</SelectItem>
                  <SelectItem value="contabilidade">Para Contabilidade</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setRelatorioDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={gerarRelatorio} className="gap-2" disabled={!empresa}>
                <Download className="h-4 w-4" />
                Gerar e Baixar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
